import { DirectiveBinding, ObjectDirective, nextTick } from 'vue';
import { saveColumnWidths, loadColumnWidths } from '@/utils/columnStorage';

interface ResizeColumnOptions {
  minWidth?: number;
  maxWidth?: number;
  handleWidth?: number;
  handleColor?: string;
}

function applyResizers(
  el: HTMLElement,
  binding: DirectiveBinding<ResizeColumnOptions | undefined>,
) {
  const options = {
    minWidth: binding.value?.minWidth ?? 40,
    maxWidth: binding.value?.maxWidth ?? Infinity,
    handleWidth: binding.value?.handleWidth ?? 2,
    handleColor: binding.value?.handleColor ?? 'rgba(0, 0, 0, 0.1)',
  };

  const table = el;
  const thead = table.querySelector('thead');
  if (!thead) return;

  const headerRow = thead.querySelector('tr');
  if (!headerRow) return;

  const headerCells = Array.from(headerRow.querySelectorAll('th'));
  const allRows = Array.from(table.querySelectorAll('tr'));

  let isResizing = false;
  let startX = 0;
  let startWidth = 0;
  let columnIndex = 0;

  const columnWidths: Record<number, number> = {};

  const createResizeHandle = () => {
    const handle = document.createElement('div');
    handle.classList.add('resize-handle');
    Object.assign(handle.style, {
      position: 'absolute',
      top: '0',
      right: '0',
      width: `${options.handleWidth}px`,
      height: '100%',
      cursor: 'col-resize',
      zIndex: '10',
      backgroundColor: options.handleColor,
      transition: 'background-color 0.2s',
    });

    handle.addEventListener('mouseenter', () => {
      handle.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    });

    handle.addEventListener('mouseleave', () => {
      if (!isResizing) {
        handle.style.backgroundColor = options.handleColor;
      }
    });

    return handle;
  };

  const setColumnWidth = (colIdx: number, width: number) => {
    const clampedWidth = Math.max(options.minWidth, Math.min(width, options.maxWidth));
    columnWidths[colIdx] = clampedWidth;

    allRows.forEach((row) => {
      const cells = row.querySelectorAll('th, td');
      if (cells[colIdx]) {
        const cell = cells[colIdx] as HTMLElement;
        cell.style.width = `${clampedWidth}px`;
        cell.style.minWidth = `${clampedWidth}px`;
        cell.style.maxWidth = `${clampedWidth}px`;
      }
    });

    if (binding.arg) {
      saveColumnWidths(binding.arg, columnWidths);
    }
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const newWidth = startWidth + (e.clientX - startX);
    setColumnWidth(columnIndex, newWidth);
  };

  const onMouseUp = () => {
    isResizing = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.querySelectorAll('.resize-handle').forEach((handle) => {
      const handleElement = handle as HTMLElement;
      handleElement.style.backgroundColor = options.handleColor;
    });
  };

  const savedWidths = binding.arg ? loadColumnWidths(binding.arg) : null;

  headerCells.forEach((headerCell, idx) => {
    let initialWidth: number;

    if (savedWidths && savedWidths[idx]) {
      initialWidth = savedWidths[idx];
    } else {
      const headerText = headerCell.textContent?.trim();
      if (headerText) {
        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.whiteSpace = 'nowrap';
        tempSpan.style.font = getComputedStyle(headerCell).font;
        tempSpan.textContent = headerText;
        document.body.appendChild(tempSpan);

        initialWidth = tempSpan.offsetWidth + 50;
        document.body.removeChild(tempSpan);
      } else {
        initialWidth = 40;
      }
    }

    setColumnWidth(idx, initialWidth);

    const handle = createResizeHandle();
    Object.assign(headerCell.style, {
      position: 'relative',
      userSelect: 'none',
    });
    headerCell.appendChild(handle);

    handle.addEventListener('mousedown', (e) => {
      isResizing = true;
      startX = e.clientX;
      startWidth = headerCell.offsetWidth;
      columnIndex = idx;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      e.preventDefault();
    });
  });
}

const resizeColumns: ObjectDirective<HTMLElement, ResizeColumnOptions | undefined> = {
  async mounted(el, binding) {
    await nextTick();
    applyResizers(el, binding);
  },
  async updated(el, binding) {
    await nextTick();
    el.querySelectorAll('.resize-handle').forEach((handle) => handle.remove());
    applyResizers(el, binding);
  },
};

export default resizeColumns;
